import { z } from 'zod';
import { validDiaryDate } from './diary-date.js';
const diaryDateSchema = z.string().refine(validDiaryDate, 'Некорректная дата дневника');
import { activityLevelSchema, goalTypeSchema, mealTypeSchema, nutrientsSchema, sexSchema, } from './nutrition.js';
/* ─────────────────────────── Авторизация ─────────────────────────── */
export const authRequestSchema = z.object({
    /** Сырая строка Telegram.WebApp.initData - подпись проверяется на сервере. */
    initData: z.string().min(1),
    /**
     * Минуты к востоку от UTC. Запоминается в профиле: в личных сообщениях
     * Telegram часовой пояс не сообщает, и без него бот не поймёт, завтрак
     * сейчас у человека или ужин.
     */
    tzOffsetMinutes: z.number().int().min(-840).max(840).optional(),
});
export const userSchema = z.object({
    id: z.string().uuid(),
    telegramId: z.number(),
    username: z.string().nullable(),
    firstName: z.string().nullable(),
    languageCode: z.string().nullable(),
    photoUrl: z.string().nullable(),
});
export const authResponseSchema = z.object({
    token: z.string(),
    expiresAt: z.number(),
    user: userSchema,
    /** null, если пользователь ещё не заполнил анкету. */
    goals: z
        .object({
        dailyKcal: z.number(),
        proteinG: z.number(),
        fatG: z.number(),
        carbsG: z.number(),
    })
        .nullable(),
});
/** Имя в приложении. Ник Telegram берётся из аккаунта и здесь не меняется. */
export const updateProfileSchema = z.object({
    firstName: z.string().trim().min(1).max(64),
});
/* ──────────────────────────── Профиль ────────────────────────────── */
export const profileInputSchema = z.object({
    sex: sexSchema,
    ageYears: z.number().int().min(10).max(120),
    heightCm: z.number().min(80).max(260),
    weightKg: z.number().min(25).max(400),
    activityLevel: activityLevelSchema,
    goalType: goalTypeSchema,
    /** Если задано - перекрывает рассчитанную норму калорий. */
    customKcal: z.number().int().min(800).max(8000).nullable().optional(),
    /** Показывать цель и учитывать отдельные записи чистой воды. */
    waterTrackingEnabled: z.boolean().optional(),
});
export const goalsSchema = z.object({
    dailyKcal: z.number(),
    proteinG: z.number(),
    fatG: z.number(),
    carbsG: z.number(),
    profile: profileInputSchema.nullable(),
});
export const weightMeasurementSchema = z.object({ date: diaryDateSchema, weightKg: z.number().min(25).max(400) });
export const weightHistorySchema = z.object({ measurements: z.array(weightMeasurementSchema) });
/* ──────────────────────────── Продукты ───────────────────────────── */
export const productSchema = z.object({
    id: z.string().uuid(),
    source: z.enum(['off', 'user', 'curated']),
    sourceId: z.string().nullable(),
    barcode: z.string().nullable(),
    name: z.string(),
    brand: z.string().nullable(),
    imageUrl: z.string().nullable(),
    servingSizeG: z.number().nullable(),
    nutritionBasis: z.enum(['g', 'ml']).optional(),
    liquid: z.boolean().optional(),
    densityGPerMl: z.number().positive().nullable().optional(),
    densityEstimated: z.boolean().optional(),
    per100g: nutrientsSchema,
    verified: z.boolean(),
});
export const searchQuerySchema = z.object({
    q: z.string().trim().min(2).max(100),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    /** Искать во внешнем API, если своей базы не хватило. */
    remote: z.coerce.boolean().default(true),
});
export const barcodeParamsSchema = z.object({
    barcode: z.string().regex(/^\d{6,14}$/, 'Штрих-код - это 6–14 цифр'),
});
export const createProductSchema = z.object({
    name: z.string().trim().min(2).max(200),
    brand: z.string().trim().max(120).nullable().optional(),
    barcode: z.string().regex(/^\d{6,14}$/).nullable().optional(),
    servingSizeG: z.number().min(1).max(2000).nullable().optional(),
    /** В какой единице напечатана пищевая ценность на упаковке. */
    nutritionBasis: z.enum(['g', 'ml']).default('g'),
    per100g: nutrientsSchema,
});
/* ───────────────────────────── Дневник ───────────────────────────── */
/** Ключевые нутриенты на 100 г - в таком виде их сообщает каждый источник. */
export const coreNutrientsSchema = z.object({
    kcal: z.number(),
    protein: z.number(),
    fat: z.number(),
    carbs: z.number(),
});
/** Что дали источники по позиции - для прозрачности во всплывающем окне. */
export const itemSourcesSchema = z.object({
    /** Наша база знаний (она же вобрала Open Food Facts). null - если продукт
     *  нашёлся только в USDA (запасной источник). */
    ours: coreNutrientsSchema.nullable(),
    /** USDA, если удалось сверить или он стал источником; иначе null. */
    usda: coreNutrientsSchema.nullable(),
    /** FatSecret, если удалось сверить. У записей до его появления нет. */
    fatsecret: coreNutrientsSchema.nullable().optional(),
    /** true - в итог взято среднее нескольких источников, false - только один. */
    averaged: z.boolean(),
});
/** Продукт, найденный внутри одной фразы. */
export const entryItemSchema = z.object({
    name: z.string(),
    kind: z.enum(['food', 'water']).optional(),
    grams: z.number().nullable(),
    volumeMl: z.number().positive().nullable().optional(),
    nutritionBasis: z.enum(['g', 'ml']).optional(),
    densityGPerMl: z.number().positive().nullable().optional(),
    densityEstimated: z.boolean().optional(),
    kcal: z.number(),
    protein: z.number(),
    fat: z.number(),
    carbs: z.number(),
    /** КБЖУ на 100 единиц nutritionBasis; старое имя поля сохранено для совместимости. */
    per100g: nutrientsSchema.optional(),
    /** Снимок двух источников. У старых записей нет. */
    sources: itemSourcesSchema.optional(),
});
export const diaryEntrySchema = z.object({
    id: z.string().uuid(),
    /**
     * Что показывать в ленте: опрятное название от модели, а если его нет -
     * то, что человек написал.
     */
    text: z.string(),
    /** Исходная фраза. По ней работают правки, и её видно в разборе. */
    sourceText: z.string().optional(),
    mealType: mealTypeSchema,
    eatenAt: z.string(),
    kcal: z.number(),
    protein: z.number(),
    fat: z.number(),
    carbs: z.number(),
    /** Разбор: из чего сложилась сумма. */
    items: z.array(entryItemSchema),
});
export const addEntrySchema = z.object({
    productId: z.string().uuid(),
    grams: z.number().min(1).max(5000),
    mealType: mealTypeSchema,
    /** ISO-дата приёма пищи; по умолчанию - сейчас. */
    eatenAt: z.string().datetime().optional(),
});
/** Добавление уже найденного продукта (например, после сканирования штрихкода). */
export const addProductEntrySchema = z.object({
    date: diaryDateSchema.optional(),
    productId: z.string().uuid(),
    grams: z.number().min(1).max(5000).optional(),
    volumeMl: z.number().min(1).max(5000).optional(),
    /** Нужен серверу, чтобы выбрать завтрак/обед/ужин по времени пользователя. */
    tzOffsetMinutes: z.number().int().min(-840).max(840),
    mealType: mealTypeSchema.optional(),
}).refine((v) => (v.grams != null) !== (v.volumeMl != null), 'Укажите либо граммы, либо миллилитры');
/* ──────────────────── Добавление еды текстом ─────────────────── */
export const addByTextSchema = z.object({
    date: diaryDateSchema.optional(),
    /** Свободный текст: «100 гр овсянки, 20 г творога, столовая ложка мёда». */
    text: z.string().trim().min(2).max(500),
    /** Минуты к востоку от UTC - по ним сервер определяет приём пищи. */
    tzOffsetMinutes: z.number().int().min(-840).max(840),
    /** Если не задан, выбирается по времени суток пользователя. */
    mealType: mealTypeSchema.optional(),
});
/** Фрагмент, для которого не нашлось продукта, - показываем пользователю. */
export const unresolvedItemSchema = z.object({
    text: z.string(),
    reason: z.string(),
});
export const daySummarySchema = z.object({
    date: z.string(),
    /** Дней подряд с записями в дневнике - считается на сегодня, не на date. */
    streak: z.number().int().min(0),
    consumed: z.object({
        kcal: z.number(),
        protein: z.number(),
        fat: z.number(),
        carbs: z.number(),
    }),
    goals: z.object({
        kcal: z.number(),
        proteinG: z.number(),
        fatG: z.number(),
        carbsG: z.number(),
    }),
    water: z.object({
        enabled: z.boolean(),
        consumedMl: z.number().min(0),
        goalMl: z.number().positive(),
    }),
    entries: z.array(diaryEntrySchema),
});
export const addByTextResponseSchema = z.object({
    /** null, если ни одна позиция не опознана - записывать нечего. */
    entry: diaryEntrySchema.nullable(),
    unresolved: z.array(unresolvedItemSchema),
    mealType: mealTypeSchema,
});
export const dayQuerySchema = z.object({
    /** YYYY-MM-DD в часовом поясе пользователя. */
    date: diaryDateSchema.optional(),
    tzOffsetMinutes: z.coerce.number().int().min(-840).max(840).default(0),
});
/* ──────────────────────── Сохранённые блюда ──────────────────────── */
/** Готовое блюдо: состав разобран один раз, БЖУ посчитаны и лежат целиком. */
export const savedMealSchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    kcal: z.number(),
    protein: z.number(),
    fat: z.number(),
    carbs: z.number(),
    /** Разбор на позиции - из чего сложилась сумма. */
    items: z.array(entryItemSchema),
});
export const createSavedMealSchema = z.object({
    /** Название, которое человек даёт блюду сам. */
    title: z.string().trim().min(1).max(80),
    /** Состав свободным текстом - его разбирает тот же движок, что и дневник. */
    text: z.string().trim().min(2).max(500),
});
export const createSavedMealResponseSchema = z.object({
    /** null, если ни одна позиция не опознана - сохранять нечего. */
    meal: savedMealSchema.nullable(),
    unresolved: z.array(unresolvedItemSchema),
});
/** Добавление готового блюда в дневник: без ИИ, приём пищи - по времени суток. */
export const addSavedMealSchema = z.object({
    date: diaryDateSchema.optional(),
    tzOffsetMinutes: z.number().int().min(-840).max(840),
    mealType: mealTypeSchema.optional(),
});
/* ─────────────────── Правка записи свободным текстом ─────────────────── */
/** Одна правка позиции: новые граммы и/или новые нутриенты на 100 г. */
export const correctionChangeSchema = z.object({
    /** Индекс позиции в записи (0-based). */
    item: z.number().int().min(0),
    grams: z.number().positive().max(5000).nullable().optional(),
    volumeMl: z.number().positive().max(5000).nullable().optional(),
    nutritionBasis: z.enum(['g', 'ml']).optional(),
    kcal100: z.number().min(0).max(1000).nullable().optional(),
    protein100: z.number().min(0).max(100).nullable().optional(),
    fat100: z.number().min(0).max(100).nullable().optional(),
    carbs100: z.number().min(0).max(100).nullable().optional(),
}).refine((v) => !(v.grams != null && v.volumeMl != null), 'Укажите только одну единицу количества');
/** Запрос на разбор правки: «съел 250 г, а не 200», «калорий больше». */
export const correctionRequestSchema = z.object({
    text: z.string().trim().min(2).max(300),
});
/** Предпросмотр: что ИИ понял и как будет выглядеть запись после правки. */
export const correctionPreviewSchema = z.object({
    changes: z.array(correctionChangeSchema),
    /** Запись с уже применённой правкой - но ещё не сохранённая. */
    entry: diaryEntrySchema,
});
/** Применение правки: клиент возвращает changes, полученные в предпросмотре. */
export const correctionApplySchema = z.object({
    changes: z.array(correctionChangeSchema).min(1).max(30),
});
//# sourceMappingURL=api.js.map